import React, { useEffect, useState } from 'react';
import {
  Search,
  Filter,
  Edit,
  Trash2,
  MoreVertical,

  Download,
  RefreshCw,
  Shield,
  GraduationCap,
  UserCheck,
  Star,
  Cpu
} from 'lucide-react';
import { User } from '../../types/auth';
import { userService } from '../../services/database/userService';
import { toast } from '../../utils/toast';
import UserEditModal from '../../components/admin/UserEditModal';
import { useAuth } from '../../contexts/AuthContext';

/**
 * User Management Page
 * Quản lý toàn bộ người dùng trong hệ thống
 */
const UserManagement: React.FC = () => {
  const { user: currentUser, isLoading: authLoading, isAuthenticated } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'student' | 'teacher' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [backendInfo, setBackendInfo] = useState<{ configured: string; active: string; healthy: boolean } | null>(null);

  /* Server-side Pagination State */
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [limit] = useState(100); // Requested: 100 per page

  // Debounce search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    // Reset to page 1 when filters change (except page itself)
    setPage(1);
  }, [debouncedSearchTerm, roleFilter, statusFilter, domainFilter]);

  // ✅ Load users via Server-Side Pagination
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentUser) {
      loadUsers();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentUser, page, debouncedSearchTerm, roleFilter, statusFilter, domainFilter]);

  // Remove the old local filtering effect
  // useEffect(() => { filterUsers(); }, [users, searchTerm, roleFilter, statusFilter]); -> DELETED

  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log(`📥 [UserManagement] Loading Page ${page}...`);

      // Check backend info (once, separate effect or just check here)
      // Usually good to keep it lighter, but let's keep it for now
      if (!backendInfo) {
        const info = userService.getBackendInfo();
        setBackendInfo(info);
      }

      const result = await userService.getAllUsers({
        page: page,
        limit: limit,
        role: roleFilter,
        status: statusFilter,
        search: debouncedSearchTerm,
        source_domain: domainFilter,
      });

      console.log('✅ [UserManagement] Load success:', result.users.length, 'users');
      console.log('📊 Total:', result.total, 'users found');

      setUsers(result.users);
      // We don't use filteredUsers anymore, users IS the current page
      setFilteredUsers(result.users); // Keep for compatibility if other code uses it, but logic is server-side
      setTotalUsersCount(result.total);
      setTotalPages(Math.ceil(result.total / limit) || 1);

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    } catch (error) {
      console.error('❌ [UserManagement] Error loading users:', error);
      toast.error('Không thể tải danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  // Replaces functionality of filterUsers
  // We don't strictly need this function anymore since loadUsers handles it
  // But to minimize code breakage below, we can ensure 'filteredUsers' is kept in sync or replaced
  // Actually, let's just use 'users' for rendering in the table.

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userToDelete: User) => {
    if (userToDelete.id === currentUser?.id || userToDelete.email === currentUser?.email) {
      toast.error('Không thể xóa chính bạn!');
      return;
    }

    if (!confirm(`⚠️ BẠN CÓ CHẮC MUỐN XÓA NGƯỜI DÙNG NÀY?\n\nTên: ${userToDelete.name}\nEmail: ${userToDelete.email}\nVai trò: ${userToDelete.role}\n\nHành động này KHÔNG THỂ HOÀN TÁC!`)) {
      return;
    }

    try {
      await userService.deleteUser(userToDelete.id);
      toast.success(`Đã xóa người dùng "${userToDelete.name}" thành công`);
      loadUsers(); // Reload current page
    } catch (error) {
      console.error('❌ [UserManagement] Error deleting user:', error);
      toast.error('Không thể xóa người dùng');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await userService.updateUser(user.id, {
        isActive: !user.isActive
      });
      toast.success(`Đã ${!user.isActive ? 'kích hoạt' : 'vô hiệu hóa'} người dùng`);
      loadUsers(); // Reload to reflect status (and maybe it disappears if filtered by status)
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Không thể cập nhật trạng thái');
    }
  };

  const exportToCSV = () => {
    // Export current view or all? Usually export matches filter but maybe all pages.
    // For now export current page users to keep it simple, or warn.
    const headers = ['ID', 'Tên', 'Email', 'Vai trò', 'Trạng thái', 'Ngày tạo'];
    const rows = users.map(u => [
      u.id,
      u.name,
      u.email,
      u.role,
      u.isActive ? 'Hoạt động' : 'Vô hiệu',
      new Date(u.createdAt).toLocaleDateString('vi-VN')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_page_${page}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Đã export danh sách (Trang hiện tại)');
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'teacher': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'student': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'teacher': return <UserCheck className="w-4 h-4" />;
      case 'student': return <GraduationCap className="w-4 h-4" />;
      default: return null;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'teacher': return 'Giáo viên';
      case 'student': return 'Học sinh';
      default: return role;
    }
  };

  const getDomainBadge = (domain?: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      ebook:    { label: '📚 Ebook',    cls: 'bg-blue-100 text-blue-800' },
      lab:      { label: '⚗️ Lab',      cls: 'bg-green-100 text-green-800' },
      tronde:   { label: '📝 Trộn đề', cls: 'bg-orange-100 text-orange-800' },
      eng:      { label: '🌐 Eng',      cls: 'bg-purple-100 text-purple-800' },
      vatli365: { label: '🏠 VatLi365', cls: 'bg-gray-200 text-gray-700' },
    };
    return domain && map[domain]
      ? map[domain]
      : { label: 'Chưa rõ', cls: 'bg-gray-100 text-gray-400' };
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quản lý thành viên</h1>
          <p className="text-gray-600 mt-2">
            Tổng số: <span className="font-semibold">{totalUsersCount}</span> người dùng
            (Trang {page}/{totalPages})
          </p>
        </div>
        <div className="flex items-center space-x-3 flex-wrap">
          <button
            onClick={() => loadUsers()}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Làm mới</span>
          </button>

          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            <Download className="w-4 h-4" />
            <span>Export Page CSV</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Domain Filter */}
          <select
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            className="px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50 text-blue-800 font-medium appearance-none"
          >
            <option value="all">🌐 Tất cả nguồn</option>
            <option value="ebook">📚 Ebook</option>
            <option value="lab">⚗️ Lab</option>
            <option value="tronde">📝 Trộn đề</option>
            <option value="eng">🌐 Eng</option>
            <option value="vatli365">🏠 VatLi365</option>
          </select>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">Tất cả vai trò</option>
              <option value="student">Học sinh</option>
              <option value="teacher">Giáo viên</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="active">Hoạt động</option>
            <option value="inactive">Vô hiệu</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Người dùng
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Vai trò
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Ngày tạo
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Lần đăng nhập cuối
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Nguồn
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Trạng thái PRO
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    Không tìm thấy người dùng nào
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {user.avatar ? (
                          <img
                            src={user.avatar}
                            alt={user.name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-sm">
                              {user.name[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleColor(user.role)}`}>
                        {getRoleIcon(user.role)}
                        <span>{getRoleLabel(user.role)}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition ${user.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                      >
                        {user.isActive ? 'Hoạt động' : 'Vô hiệu'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString('vi-VN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.lastLoginAt
                        ? new Date(user.lastLoginAt).toLocaleString('vi-VN')
                        : 'Chưa đăng nhập'}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const badge = getDomainBadge((user as any).source_domain);
                        return (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        {/* Ebook PRO */}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-400 w-12">Ebook:</span>
                          {user.permissions?.pro ? (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border border-purple-200">
                              <Star className="w-2.5 h-2.5" fill="currentColor" />
                              <span>PRO</span>
                            </span>
                          ) : (
                            <span className="text-gray-300 text-[10px]">-</span>
                          )}
                        </div>
                        
                        {/* Lab PRO */}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-400 w-12">Lab:</span>
                          {user.permissions?.lab_pro ? (
                            <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-100 to-emerald-100 text-blue-700 border border-blue-200">
                              <Cpu className="w-2.5 h-2.5" />
                              <span>PRO</span>
                            </span>
                          ) : (
                            <span className="text-gray-300 text-[10px]">-</span>
                          )}
                        </div>

                        {/* Pending Request */}
                        {user.permissions?.pro_request?.status === 'pending' && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-700 border border-orange-200 animate-pulse">
                              Pending: {user.permissions.pro_request.plan}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Chỉnh sửa"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {/* Không cho xóa chính mình */}
                        {user.id !== currentUser?.id && user.email !== currentUser?.email ? (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Xóa"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <div className="p-2 text-gray-300 cursor-not-allowed" title="Không thể xóa chính bạn">
                            <Trash2 className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 sm:px-6 rounded-xl mt-4 shadow-sm">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Trước
          </button>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="relative ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Sau
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Hiển thị <span className="font-medium">{(page - 1) * limit + 1}</span> đến <span className="font-medium">{Math.min(page * limit, totalUsersCount)}</span> trong số <span className="font-medium">{totalUsersCount}</span> kết quả
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">First</span>
                ««
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                «
              </button>

              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                Trang {page} / {totalPages}
              </span>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                »
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="sr-only">Last</span>
                »»
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {
        showEditModal && selectedUser && (
          <UserEditModal
            user={selectedUser}
            onClose={() => {
              setShowEditModal(false);
              setSelectedUser(null);
            }}
            onSave={loadUsers}
          />
        )
      }
    </div >
  );
};

export default UserManagement;

